import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5078">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5079">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5080">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5081">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5082" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5083">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5084" />
              <p className="text-sm text-gray-600" data-id="element-5085">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5086">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5087" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5088">
          {/* Quality & Metadata */}
          <div data-id="element-5089">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5090">
              <Brain className="w-3.5 h-3.5" data-id="element-5091" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5092">
              <div className="grid grid-cols-2 gap-4" data-id="element-5093">
                <div data-id="element-5094">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5095">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5096">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5097">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5098">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5099">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5100">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5101">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5102">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5103">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5104">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5105">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5106">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5107">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5108">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5109">
              <Database className="w-3.5 h-3.5" data-id="element-5110" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5111">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5112" />
                <span className="text-sm text-gray-600" data-id="element-5113">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5114">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5115">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5116">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5117">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5118" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5119">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5120">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5121" />
                  <span className="text-sm" data-id="element-5122">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5123">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5124" />
                  <span className="text-sm" data-id="element-5125">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5126">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5127">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5128" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5129">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5130">
                      <div data-id="element-5131">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5132">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5133">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5134">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5135">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5136">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5137">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5138">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5139">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5140">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5141">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5142" />
                        <span className="text-sm text-gray-600" data-id="element-5143">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5144">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5145" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5146" />}
                        <span className="text-sm text-gray-600" data-id="element-5147">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}